import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateBatteryPrice } from "./battery-price-dependents"
import type {
  BatteryLightReachOffersDto,
  BatteryProposalFinancingDto,
  SaveBatteryLightReachOfferDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/**
 * Save a returned LightReach Energy Backup offer. The save moves the
 * pricing-input generation and may already have staled the previous Quote
 * when it fails, so both outcomes refresh everything downstream of the price.
 */
export default function useSaveBatteryLightReachOffer(): UseMutationResult<
  BatteryProposalFinancingDto,
  unknown,
  { proposalId: string; body: SaveBatteryLightReachOfferDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      batteryProposalService.saveLightReachOffer(proposalId, body),
    onError: (_error, { proposalId }) => {
      invalidateBatteryPrice(queryClient, proposalId)
    },
    onSuccess: (financing, { proposalId }) => {
      // The response is the financing read after the offer was saved.
      queryClient.setQueryData(
        queryKeys.batteryProposal.financing(proposalId),
        financing,
      )
      // Until the offers re-read lands, the cached read still names the
      // replaced offer as saved. Point it at the offer the save just wrote.
      const saved = financing.quote?.loanProduct?.lightReach
      if (saved) {
        queryClient.setQueryData<BatteryLightReachOffersDto>(
          queryKeys.batteryProposal.lightReachOffers(proposalId),
          (offers) => (offers ? { ...offers, selectedOffer: saved } : offers),
        )
      }
      invalidateBatteryPrice(queryClient, proposalId)
    },
  })
}
